import { useEffect, useState, type FormEvent } from 'react'
import { KeyRound, ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/primitives'
import { usePlatformSession } from '@/stores/platformSession'
import { ApiError } from '@/lib/api'

/** Mirrors PlatformAuthenticationService.MinimumPasswordLength — the server is the authority. */
const MINIMUM_LENGTH = 12

interface ChangePasswordDialogProps {
  open: boolean
  /**
   * True when the panel is holding until this is done — the seeded password is still in force.
   * There is no way out of the dialog in that state, and no cancel button.
   */
  forced: boolean
  onClose: () => void
  /**
   * Fired the moment the change lands, before the dialog is closed.
   *
   * A forced change clears the flag that was holding the dialog open, so without this the panel
   * would unmount it mid-success and the operator would never see that their other sessions had
   * just been ended — which is the one consequence worth telling them about.
   */
  onChanged: () => void
}

/**
 * An operator changing their own platform password.
 *
 * This is the most privileged credential in the system — it provisions and drops whole shop
 * databases — and it had no way to be rotated at all until now: the only password it ever had was
 * the one the first startup generated and wrote to the application log, where it stays for as long
 * as the logs are kept.
 *
 * The current password is asked for even though the operator is plainly signed in. A live session
 * proves somebody signed in a few minutes ago; it does not prove they are the operator rather than
 * whoever sat down at the machine while it was unlocked.
 */
export function ChangePasswordDialog({
  open,
  forced,
  onClose,
  onChanged,
}: ChangePasswordDialogProps) {
  const changePassword = usePlatformSession((state) => state.changePassword)

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  // Cleared on the way in rather than on the way out, so a password never sits in component state
  // behind a closed dialog waiting for a re-render to show it.
  useEffect(() => {
    if (open) {
      setCurrent('')
      setNext('')
      setConfirm('')
      setError(null)
      setDone(false)
    }
  }, [open])

  const mismatch = confirm !== '' && next !== confirm
  const tooShort = next !== '' && next.trim().length < MINIMUM_LENGTH
  const ready = current !== '' && next !== '' && !mismatch && !tooShort

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSaving(true)

    try {
      await changePassword(current, next)
      setDone(true)
      onChanged()
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? (caught.detail ?? caught.message)
          : 'Could not reach the server. Check the connection and try again.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      // A forced change has no way past it — no close button, no backdrop click, no Escape.
      // Anything else leaves the panel usable on a password that is sitting in a log file, which
      // is the thing being fixed.
      dismissible={!forced || done}
      onClose={onClose}
      title={forced ? 'Choose a password' : 'Change your password'}
    >
      {done ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm">
            Done. Every other device signed in as you has been signed out — this one stayed in.
          </p>
          <Button onClick={onClose} full>
            Close
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          {forced && (
            <div className="flex gap-2 rounded-lg bg-warn-500/10 px-3 py-2 text-sm">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warn-500" />
              <p>
                The password you signed in with was generated at startup and written to the server
                log. Choose one of your own before going on.
              </p>
            </div>
          )}

          <Input
            type="password"
            label="Current password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            autoComplete="current-password"
            autoFocus
            leading={<KeyRound className="h-4 w-4" />}
          />

          <Input
            type="password"
            label="New password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            autoComplete="new-password"
            hint={`At least ${MINIMUM_LENGTH} characters. This login can drop a shop's database.`}
            error={tooShort ? `Use at least ${MINIMUM_LENGTH} characters.` : undefined}
          />

          <Input
            type="password"
            label="New password again"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            error={mismatch ? 'The two do not match.' : undefined}
          />

          {error && (
            <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            {!forced && (
              <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={!ready || isSaving}>
              {isSaving ? 'Changing…' : 'Change password'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  )
}
